import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { api } from "../api/client";

const AuthContext = createContext(null);

// Roles that route to the external client (law-firm) portal layout.
const EXTERNAL_ROLES = new Set(['CLIENT', 'FIRM_USER', 'FIRM_ADMIN']);
const isExternalRole = (role) => EXTERNAL_ROLES.has(String(role || '').toUpperCase());
// Roles that route to the standalone clinic portal (Phase 1C).
const isClinicRole = (role) => String(role || '').toUpperCase() === 'CLINIC';
// Partner view (2026-10): outside referral partners, read-only shell.
const isPartnerRole = (role) => String(role || '').toUpperCase() === 'PARTNER';

// Storage key constants
const TOKEN_KEY = "token";
const USER_KEY = "user";

// Helper to get token from either storage (migration support)
const getStoredToken = () => {
  return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
};

// Helper to get user from either storage (migration support)
const getStoredUser = () => {
  const sessionUser = sessionStorage.getItem(USER_KEY);
  const localUser = localStorage.getItem(USER_KEY);
  return sessionUser || localUser;
};

// ── Framed-only token persistence (2026-08) ────────────────────────────────
// Merritt-authorized SCOPED EXCEPTION to the sessionStorage-for-security
// design. Inside a Microsoft Teams tab, navigating away unmounts the iframe
// and destroys sessionStorage, forcing a re-login on every return. When the
// portal runs FRAMED (window.self !== window.top) the token persists in
// localStorage instead — which survives iframe unmounts and is partitioned
// by the browser per top-level site. Inactivity logout is governed by JWT
// expiry exactly as before (no lifetime change). In a normal top-level
// browser the behavior is byte-identical to today: sessionStorage-only,
// including the localStorage cleanup migration. Reads already check both
// stores everywhere (sessionStorage || localStorage), so no read changes
// are needed. This exception is SUPERSEDED when Teams SSO lands.
const isFramed = () => {
  try { return window.self !== window.top; } catch (e) { return true; }
};

// Helper to set auth data (sessionStorage for security; framed = scoped exception above)
const setAuthStorage = (token, userData) => {
  if (isFramed()) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    // Clear any sessionStorage copies so exactly one store holds the token
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    resetActivity(); // fresh login = fresh idle clock
    console.info("[WS-AUTH] wrote framed", { instance: BOOT_INSTANCE_ID }); // TEMPORARY diagnostics
    return;
  }
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(USER_KEY, JSON.stringify(userData));
  // Remove from localStorage if present (migration)
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  resetActivity(); // fresh login = fresh idle clock
};

// Persist an updated user object into whichever store is active
// (single write path — keeps framed sessions from going stale).
const persistUserData = (userData) => {
  const store = isFramed() ? localStorage : sessionStorage;
  store.setItem(USER_KEY, JSON.stringify(userData));
};

// ── Automatic logoff (2026-08) ──────────────────────────────────────────────
// HIPAA Security Rule automatic-logoff specification,
// 45 CFR § 164.312(a)(2)(iii): 15 minutes of inactivity → logout, with a
// warning at 13. 15 matches the long-standing value of the retired
// in-memory SessionTimeoutManager timer — this mechanism hardens it
// (persistent across remounts/refreshes, Teams-aware) rather than changing
// it. The 8-hour ABSOLUTE token expiry
// (ACCESS_TOKEN_EXPIRE_MINUTES) is unchanged — defense in depth: idle
// governs walk-aways, absolute bounds total lifetime.
// Per-role values are a future one-line change.
export const IDLE_TIMEOUT_MINUTES = 15;
export const IDLE_WARNING_MINUTES = 13;
const ACTIVITY_KEY = "last_activity";
// The timestamp rides the SAME store as the token (framed = localStorage,
// per the Teams-persistence scoped exception) so Teams iframe remounts
// neither reset nor lose it — a remount is NOT activity.
const activityStore = () => (isFramed() ? localStorage : sessionStorage);
let _lastActivityWrite = 0;
export const touchActivity = () => {
  const now = Date.now();
  if (now - _lastActivityWrite < 30000) return; // throttle: at most 1 write / 30s
  _lastActivityWrite = now;
  activityStore().setItem(ACTIVITY_KEY, String(now));
};
export const resetActivity = () => {
  _lastActivityWrite = Date.now();
  activityStore().setItem(ACTIVITY_KEY, String(_lastActivityWrite));
};
export const idleMinutes = () => {
  const raw = activityStore().getItem(ACTIVITY_KEY);
  if (!raw) return 0;
  return (Date.now() - Number(raw)) / 60000;
};
export const hasActivityStamp = () => activityStore().getItem(ACTIVITY_KEY) !== null;

// ── TEMPORARY Teams boot diagnostics (2026-08) — REMOVE AFTER DIAGNOSIS ────
// Field finding: inside Teams web (top-level site cloud.microsoft) an
// iframe remount renders the login screen while a valid token+user sit
// readable in the frame's localStorage partition. Hypothesis: storage-view
// divergence between Teams' cacheable wrapper frame and the GUID frame.
// LOGGING ONLY — zero behavior changes. Gated on isFramed(): top-level
// users emit nothing. Booleans/lengths only — NEVER token/user values.
const BOOT_INSTANCE_ID = Math.random().toString(36).slice(2, 10);
const _safe = (fn) => {
  try { return fn(); } catch (e) { return `ERR:${(e && e.name) || "unknown"}`; }
};
const _diagSnapshot = () => ({
  instance: BOOT_INSTANCE_ID,
  href: _safe(() => window.location.href),
  referrer: _safe(() => document.referrer),
  sessionTokenPresent: _safe(() => sessionStorage.getItem(TOKEN_KEY) !== null),
  localTokenPresent: _safe(() => localStorage.getItem(TOKEN_KEY) !== null),
  sessionUserPresent: _safe(() => sessionStorage.getItem(USER_KEY) !== null),
  localUserPresent: _safe(() => localStorage.getItem(USER_KEY) !== null),
  stamp: _safe(() => activityStore().getItem(ACTIVITY_KEY)),
  idleMinutes: _safe(() => idleMinutes()),
});
const diagBoot = (branch) => {
  if (!isFramed()) return;
  _safe(() => console.info("[WS-BOOT]", { branch, ..._diagSnapshot() }));
};
const diagCleared = () => {
  if (!isFramed()) return;
  const caller = _safe(() =>
    String(new Error().stack || "").split("\n").slice(1, 3).join(" <- ").trim()
  );
  _safe(() => console.info("[WS-AUTH] cleared", { instance: BOOT_INSTANCE_ID, caller }));
};
// ── end TEMPORARY diagnostics ───────────────────────────────────────────────

// Helper to clear auth data from both storages
export const clearAuthStorage = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  // Idle-timestamp rides along: a cleared session must not leave a stamp
  sessionStorage.removeItem(ACTIVITY_KEY);
  localStorage.removeItem(ACTIVITY_KEY);
  diagCleared(); // TEMPORARY diagnostics (framed-only, logs caller)
};

// Map a TokenResponse from /auth/login (non-MFA path) or
// /auth/mfa/verify into the shape `AuthProvider` stores. Extracted so
// both call sites use identical handling — keeps the MFA branch a pure
// pass-through with zero behavioural drift from the non-MFA path.
const _buildUserDataFromTokenResponse = (data) => {
  const {
    role,
    username,
    email,
    user_id,
    user_type,
    tenant_id,
    must_change_password,
    location_id,
  } = data;
  return {
    id: user_id,
    username,
    email,
    role,
    // Defensive: backend should always send `user_type`, but if it's missing
    // (legacy account, sync glitch) infer it from role so external users
    // never silently land in the internal portal layout.
    userType: user_type || (isExternalRole(role) ? 'external' : 'internal'),
    tenantId: tenant_id || null,
    must_change_password: !!must_change_password,
    location_id: location_id || null,
  };
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getStoredToken();
    const saved = getStoredUser();
    if (token && saved) {
      // Mount/remount idle check (e.g. returning to a Teams tab hours
      // later): a stale timestamp logs out IMMEDIATELY — no flash of
      // authenticated content. A remount is NOT activity (no touch here).
      if (idleMinutes() >= IDLE_TIMEOUT_MINUTES) {
        diagBoot("stale-logout"); // TEMPORARY diagnostics
        clearAuthStorage();
        setLoading(false);
        return;
      }
      try {
        const userData = JSON.parse(saved);
        // Defensive: backfill userType for sessions saved before the role-based
        // inference was added. Without this, an external user who's still
        // logged in from before the fix would keep being routed to the internal
        // layout until they log out.
        if (!userData.userType && userData.role) {
          userData.userType = isExternalRole(userData.role) ? 'external' : 'internal';
        }
        setUser(userData);
        diagBoot("restored"); // TEMPORARY diagnostics
        // Migrate to sessionStorage if still in localStorage — TOP-LEVEL
        // ONLY. Framed sessions legitimately live in localStorage (Teams
        // persistence); re-running setAuthStorage on a framed remount would
        // resetActivity() and wrongly count the remount as activity.
        if (!isFramed() && localStorage.getItem(TOKEN_KEY)) {
          setAuthStorage(token, userData);
        }
      } catch (e) {
        diagBoot("parse-error"); // TEMPORARY diagnostics
        console.error("Failed to parse stored user:", e);
        clearAuthStorage();
      }
    } else {
      diagBoot("no-token"); // TEMPORARY diagnostics — snapshot shows which piece is missing
    }
    setLoading(false);
  }, []);

  /**
   * Unified login supporting both:
   * - Internal users: { username, password }
   * - External users: { email, password }
   *
   * MFA handling: if the user has MFA enabled, the backend returns
   * `{mfa_required: true, mfa_token: "<challenge jwt>"}` (no
   * access_token). We bubble that up to the caller so the Login page
   * can render the MFA prompt — we do NOT set `user` here in that
   * case. The caller follows up with `mfaVerify(mfa_token, code)`.
   */
  const login = useCallback(async (usernameOrEmail, password) => {
    // Determine if it's an email or username
    const isEmail = usernameOrEmail.includes('@');
    const payload = isEmail 
      ? { email: usernameOrEmail, password } 
      : { username: usernameOrEmail, password };
    
    const res = await api.post("/auth/login", payload);

    // MFA challenge: backend withheld the real token. Don't store
    // anything; let the caller drive the second-factor screen.
    if (res.data && res.data.mfa_required) {
      return { mfaRequired: true, mfaToken: res.data.mfa_token };
    }

    const userData = _buildUserDataFromTokenResponse(res.data);
    setAuthStorage(res.data.access_token, userData);
    setUser(userData);
    return userData;
  }, []);

  /**
   * Second-factor verification. Called by the Login page after the
   * server returned an mfa_required challenge. `code` is either a
   * 6-digit TOTP or an NNNN-NNNN-NNNN recovery code — the backend
   * decides which path to take.
   */
  const mfaVerify = useCallback(async (mfaToken, code) => {
    const res = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code });
    const userData = _buildUserDataFromTokenResponse(res.data);
    setAuthStorage(res.data.access_token, userData);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    clearAuthStorage();
    setUser(null);
  }, []);

  const clearMustChangePassword = useCallback(() => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, must_change_password: false };
      persistUserData(updated);
      return updated;
    });
  }, []);

  // Helper to check if user is internal (WestStar staff)
  const isInternalUser = user?.userType === 'internal';
  
  // Helper to check if user is external (law firm/tenant)
  // A law-firm role is external whatever user_type it carries (2026-10);
  // a partner is never routed to the law-firm portal.
  const isExternalUser = (user?.userType === 'external' || isExternalRole(user?.role))
    && !isClinicRole(user?.role) && !isPartnerRole(user?.role);

  // Partner view (2026-10): role partner only.
  const isPartnerUser = isPartnerRole(user?.role);

  // Helper to check if the user is a CLINIC portal user (Phase 1C)
  const isClinicUser = isClinicRole(user?.role);

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      mfaVerify,
      logout, 
      loading, 
      clearMustChangePassword,
      isInternalUser,
      isExternalUser,
      isClinicUser,
      isPartnerUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
